import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ChatFormOperation, IChatForm } from '@nimbus-global/features/chat/models/chat-form.model';
import { IChatFormFieldAction, IChatFormValueUpdate } from '@nimbus-global/features/chat/models/chat-form-resolver.model';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { ManageEntryGroupType, ManageEntryType, ManageRouteMode } from '@nimbus-global/features/manage/model/manage-route.model';
import { CompanionReferenceType } from '@nimbus-library/features/companion-chat/models/companion-conversation.model';
import { IOptionItem } from '@nimbus-library/features/shared/models/interfaces/i-option-item';
import { NimbusPermission } from '@nimbus-global/features/permissions/models/enums/nimbus-permission.enum';
import { OrganizationUnitService } from '@nimbus-global/features/shared/services/api/organization-unit/organization-unit.service';
import { BotConfigurationService } from '@nimbus-global/features/manage/services/bot-configuration.service';
import { VirtualUserService } from '@nimbus-global/features/manage/services/virtual-user.service';
import { IChatFormEntityHandler } from './i-chat-form-entity-handler';

@Injectable({ providedIn: 'root' })
export class VirtualUserChatFormEntityHandler implements IChatFormEntityHandler {
  private readonly nameMaxLength = 256;
  public readonly entity = CompanionReferenceType.VirtualUser;
  public readonly formDescription = TranslationKeys.CompanionVirtualUserFormDescription;
  public readonly formValueUpdate$: Observable<IChatFormValueUpdate>;

  public constructor(
    private readonly organizationUnitService: OrganizationUnitService,
    private readonly botConfigurationService: BotConfigurationService,
    private readonly virtualUserService: VirtualUserService,
  ) {
    this.formValueUpdate$ = this.initializeFormValueUpdate();
  }

  public resolveReferenceOptions(
    referenceType: string,
    form: IChatForm,
    currentValues?: Record<string, unknown>,
  ): Observable<IOptionItem[]> {
    const permission = this.getPermission(form);
    const selectedOrganizationUnitId = currentValues?.organizationUnitId as string;
    if (referenceType === CompanionReferenceType.OrganizationUnit) {
      return this.organizationUnitService.getForCreateDropDownWithoutServices([permission]);
    }
    if (!selectedOrganizationUnitId) {
      return of([]);
    }
    const organizationUnits$ = this.organizationUnitService.getOrganizationUnitWithParents(selectedOrganizationUnitId, [permission]);
    switch (referenceType) {
      case CompanionReferenceType.BotConfiguration:
        return this.botConfigurationService.botConfigurationsChanged$.pipe(
          startWith(null),
          switchMap(() => organizationUnits$.pipe(switchMap((ous) => this.botConfigurationService.getBotConfigurations(ous)))),
        );
      case CompanionReferenceType.VirtualUser:
        return organizationUnits$.pipe(switchMap((ous) => this.virtualUserService.getVirtualAgents(ous)));
      default:
        return of([]);
    }
  }

  public resolveFormTitle(operation: ChatFormOperation): TranslationKeys {
    switch (operation) {
      case ChatFormOperation.Update:
        return TranslationKeys.CompanionVirtualUserUpdateFormTitle;
      case ChatFormOperation.Read:
        return TranslationKeys.CompanionVirtualUserReadFormTitle;
      default:
        return TranslationKeys.CompanionVirtualUserFormTitle;
    }
  }

  public resolveFieldTitle(field: string): TranslationKeys {
    switch (field) {
      case 'name':
        return TranslationKeys.GeneralName;
      case 'organizationUnitId':
        return TranslationKeys.GeneralOrganizationUnit;
      case 'description':
        return TranslationKeys.GeneralDescription;
      case 'botConfigurationId':
        return TranslationKeys.ManageVirtualUserBotConfiguration;
      case 'botResponseTemplateId':
        return TranslationKeys.GeneralBotResponseTemplate;
      case 'botRequestTemplateId':
        return TranslationKeys.GeneralBotRequestTemplate;
      case 'profile':
        return TranslationKeys.ManageVirtualUserProfile;
      case 'systemInstruction':
        return TranslationKeys.ManageVirtualUserSystemInstruction;
      case 'initialMessage':
        return TranslationKeys.ManageVirtualUserInitialMessage;
      case 'closingMessage':
        return TranslationKeys.ManageVirtualUserClosingMessage;
      case 'sessionTimeoutInSeconds':
        return TranslationKeys.ManageVirtualUserSessionTimeout;
      default:
        return TranslationKeys.GeneralName;
    }
  }

  public resolveFieldAction(field: string, currentValues?: Record<string, unknown>): IChatFormFieldAction | null {
    switch (field) {
      case 'botConfigurationId': {
        const organizationUnitId = currentValues?.organizationUnitId as string | undefined;
        if (!organizationUnitId) {
          return null;
        }
        return {
          icon: ICONS.ADD_LIGHT,
          tooltip: TranslationKeys.GeneralCreate,
          routerLink: ['/', 'manage', ManageEntryGroupType.VirtualAssistants, ManageEntryType.Bot, ManageRouteMode.New],
          queryParams: { organizationUnitId },
        };
      }
      default:
        return null;
    }
  }

  public resolveFieldMaxLength(field: string): number | null {
    switch (field) {
      case 'name':
        return this.nameMaxLength;
      default:
        return null;
    }
  }

  private initializeFormValueUpdate(): Observable<IChatFormValueUpdate> {
    return this.botConfigurationService.botConfigurationCreated$.pipe(
      map((bot) => ({
        values: { botConfigurationId: bot.id },
        requiresValues: { organizationUnitId: bot.organizationUnitId },
      })),
    );
  }

  private getPermission(form: IChatForm): NimbusPermission {
    switch (form.operation) {
      case ChatFormOperation.Update:
        return NimbusPermission.VirtualUsersUpdate;
      case ChatFormOperation.Read:
        return NimbusPermission.VirtualUsersRead;
      default:
        return NimbusPermission.VirtualUsersCreate;
    }
  }
}
